import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable } from "rxjs";
import { environment } from "../../../environments/environment";
import { BaseRef } from "../models/BaseRef";
import {
  CreateClientRequestTaskRequest,
  CreateComplaintTaskRequest,
  CreatePolicyTerminationOperationalTaskRequestFromMember,
} from "../models/operational-tasks";

const POLICY_TERMINATION_MEMBER_ENDPOINT = "/me/policy-termination";
const CLIENT_REQUEST_MEMBER_ENDPOINT = "/me/client-request";
const COMPLAINT_REQUEST_MEMBER_ENDPOINT = "/me/complaint-request";
const DOCUMENT_TYPES_ENDPOINT = "/me/document-types";

@Injectable({
  providedIn: "root",
})
export class OperationalTasksService {
  private readonly operationalTasks = "operationaltasks";
  private readonly baseUrl: string;

  constructor(private readonly http: HttpClient) {
    this.baseUrl = environment.apiConfig.uri + this.operationalTasks;
  }

  createPolicyTerminationTask(request: CreatePolicyTerminationOperationalTaskRequestFromMember): Observable<any> {
    const formData = new FormData();
    formData.append("PolicyId", request.policyId);
    formData.append("OriginId", request.originId.toString());
    formData.append("DueDate", request.dueDate);
    formData.append("TerminationRequestedDate", request.terminationRequestedDate);
    formData.append("RequestReasonId", request.requestReasonId.toString());
    formData.append("comment", request.comment);

    request.terminationFilesDataFromMember.forEach((fileData, index) => {
      const baseKey = `FilesData[${index}]`;

      if (fileData.file) {
        formData.append(`${baseKey}.File`, fileData.file, fileData.file.name);
      }
      if (fileData.documentTypeId !== undefined) {
        formData.append(`${baseKey}.DocumentTypeId`, fileData.documentTypeId.toString());
      }
      if (fileData.documentStateId !== undefined) {
        formData.append(`${baseKey}.DocumentStateId`, fileData.documentStateId.toString());
      }
    });

    return this.http.post(`${this.baseUrl}${POLICY_TERMINATION_MEMBER_ENDPOINT}`, formData);
  }

  createClientRequestTask(request: CreateClientRequestTaskRequest): Observable<any> {
    const formData = new FormData();
    formData.append("PolicyOrQuotationId", request.policyOrQuotationId);
    formData.append("AgreementType", request.agreementType.toString());
    formData.append("OriginId", request.originId.toString());
    formData.append("RequestNature", request.requestNature.toString());
    // Optional field, used for attestation
    if (request.attestationType) {
      formData.append("AttestationType", request.attestationType);
      formData.append("IsGeneratedForMe", request.isGeneratedForMe ? "true" : "false");
    }
    formData.append("Comment", request.comment ?? "");

    if (request.filesData && request.filesData.length) {
      request.filesData.forEach((fileData, index) => {
        const baseKey = `FilesData[${index}]`;
        if (fileData.file) {
          formData.append(`${baseKey}.File`, fileData.file, fileData.file.name);
        }
        if (fileData.documentTypeId !== undefined) {
          formData.append(`${baseKey}.DocumentTypeId`, fileData.documentTypeId.toString());
        }
        if (fileData.documentStateId !== undefined) {
          formData.append(`${baseKey}.DocumentStateId`, fileData.documentStateId.toString());
        }
      });
    }

    return this.http.post(`${this.baseUrl}${CLIENT_REQUEST_MEMBER_ENDPOINT}`, formData);
  }

  createComplaintTask(request: CreateComplaintTaskRequest): Observable<any> {
    const formData = new FormData();
    formData.append("PolicyId", request.policyId);
    formData.append("OriginId", request.originId.toString());
    formData.append("RequestNature", request.requestNature.toString());
    formData.append("Comment", request.comment ?? "");

    if (request.filesData && request.filesData.length) {
      request.filesData.forEach((fileData, index) => {
        const baseKey = `FilesData[${index}]`;
        if (fileData.file) {
          formData.append(`${baseKey}.File`, fileData.file, fileData.file.name);
        }
        if (fileData.documentTypeId !== undefined) {
          formData.append(`${baseKey}.DocumentTypeId`, fileData.documentTypeId.toString());
        }
      });
    }

    return this.http.post(`${this.baseUrl}${COMPLAINT_REQUEST_MEMBER_ENDPOINT}`, formData);
  }

  getDocumentTypes(): Observable<BaseRef[]> {
    return this.http.get<BaseRef[]>(`${this.baseUrl}${DOCUMENT_TYPES_ENDPOINT}`);
  }
}
